import { entityConfigs } from '@ast-data/shared';
import { fetchAs, getToken } from '../../utils/authFetch';
import { credentialFor as keptCredentialFor } from '../../utils/pendingCredentials';
import { downloadBlob, fileNameTimestamp } from '../../utils/downloadFile';
import { zipToBlob } from '../../lib/zip';
import {
    forgetPending,
    getCurrentAuthor,
    pendingEntries
} from '../../db/authorJournal';
import { dropFromOutbox, readOutbox } from '../../db/outbox';
import { getPending, listPendingIds } from '../media/blobStore';
import { kickUpload } from '../media/mediaUploader';
import { withTimeout } from '../../utils/withTimeout';

/**
 * Getting the work off a device whose database will not open — an unpushed
 * Befund exists on that tablet and nowhere else.
 *
 * RxDB keeps each collection in its OWN browser database,
 * `rxdb-dexie-<db>--<schema version>--<collection>`, holding plain JSON in a
 * `docs` store, so a failed migration leaves the previous version readable. The
 * author journal names every document no push has accepted.
 *
 * Nothing here touches RxDB: it reads the raw stores, prefers the outbox copy,
 * and offers the result to the server and to a file. Reading only.
 */

/**
 * Every raw database of this app, generation included: when a held copy is
 * given up on (db/openStall.ts), the one left behind still holds what was never
 * uploaded — and it stays perfectly readable. A rescue that only looked at the
 * current generation would walk past exactly the records it exists for.
 */
const RAW_NAME = /^rxdb-dexie-ast_data_app(_\d+)?--/;

/** Housekeeping databases, which hold no documents of ours. */
const RAW_INTERNAL = /(_rxdb_internal|rx-replication-meta)/;

export interface SalvagedDoc {
    collection: string;
    docId: string;
    /**
     * Who wrote it, from the journal. Carried because a rescue has to travel
     * under that person's own credential — see `credentialFor`.
     */
    userId: string;
    doc: Record<string, unknown>;
    source: 'outbox' | 'database';
    /**
     * The schema version the copy was written under — the raw database's, or
     * the one the outbox noted. The server lifts the document from it; unknown
     * (an outbox entry from before the note) goes up as it is.
     */
    schemaVersion: number | undefined;
}

export interface SalvageResult {
    /** Everything found that no push has accepted. */
    found: SalvagedDoc[];
    uploaded: number;
    refused: { collection: string; docId: string; reason: string }[];
    /** True when nothing could be sent at all — offline, or not signed in. */
    offline: boolean;
    /** Photos that had not been uploaded when the rescue started. */
    photosFound: number;
    /** …and how many are still waiting now. */
    photosLeft: number;
}

/**
 * The collection each raw database belongs to, newest schema version first. The
 * version is part of the NAME, so a half-finished migration shows up as two
 * databases for one collection; both are read, newer document per id wins.
 */
async function rawDatabases(): Promise<
    { name: string; collection: string; version: number | undefined }[]
> {
    if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
        return [];
    }
    const all = await indexedDB.databases();
    const found: {
        name: string;
        collection: string;
        version: number | undefined;
    }[] = [];
    for (const entry of all) {
        const name = entry.name ?? '';
        const prefix = RAW_NAME.exec(name);
        if (!prefix || RAW_INTERNAL.test(name)) {
            continue;
        }
        const rest = name.slice(prefix[0].length);
        const split = rest.indexOf('--');
        if (split < 0) {
            continue;
        }
        const version = Number(rest.slice(0, split));
        found.push({
            name,
            // Unreadable stays unknown: sent as version 0, the server would
            // run every step over a document that may well be current.
            version:
                Number.isInteger(version) && version >= 0 ? version : undefined,
            collection: rest.slice(split + 2)
        });
    }
    return found.sort(
        // An unreadable version sorts last, behind every known one.
        (firstBackup, secondBackup) =>
            (secondBackup.version ?? -1) - (firstBackup.version ?? -1)
    );
}

/**
 * How long one raw database gets to answer. Observed, not caution: after an
 * interrupted start `indexedDB.open` never calls back at all — no success, no
 * error, no "blocked". A store that does not answer in time is skipped.
 */
const RAW_READ_TIMEOUT_MS = 3000;

/** Read one raw store. Never throws and never hangs: it is skipped instead. */
async function readRawDocs(name: string): Promise<Record<string, unknown>[]> {
    try {
        return await withTimeout(
            new Promise<Record<string, unknown>[]>((resolve) => {
                // No version: opening at the stored version, so this can never
                // trigger an upgrade on a database somebody else may need.
                const request = indexedDB.open(name);
                const done = (
                    db: IDBDatabase | null,
                    docs: Record<string, unknown>[]
                ) => {
                    db?.close();
                    resolve(docs);
                };
                request.onsuccess = () => {
                    const db = request.result;
                    if (![...db.objectStoreNames].includes('docs')) {
                        done(db, []);
                        return;
                    }
                    const all = db
                        .transaction('docs', 'readonly')
                        .objectStore('docs')
                        .getAll();
                    all.onsuccess = () =>
                        done(db, all.result as Record<string, unknown>[]);
                    all.onerror = () => done(db, []);
                };
                request.onerror = () => resolve([]);
                request.onblocked = () => resolve([]);
                // The database does not exist at this version. Nothing to read,
                // and nothing may be created here — this path only ever reads.
                request.onupgradeneeded = () => {
                    request.transaction?.abort();
                    resolve([]);
                };
            }),
            RAW_READ_TIMEOUT_MS,
            []
        );
    } catch {
        return [];
    }
}

/**
 * Throw away every store RxDB keeps for this app, one deadline each. NOT
 * `removeRxDatabase`: that goes through RxDB's internal store, which after an
 * interrupted start never calls back. Deleting by name needs nothing to be
 * readable, and a database that will not go is left rather than waited on.
 */
export async function wipeRawDatabases(): Promise<void> {
    if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
        return;
    }
    let names: string[];
    try {
        names = (
            await withTimeout(indexedDB.databases(), RAW_READ_TIMEOUT_MS, [])
        )
            .map((entry) => entry.name ?? '')
            .filter((name) => RAW_NAME.test(name));
    } catch {
        return;
    }
    await Promise.all(
        names.map((name) =>
            withTimeout(
                new Promise<void>((resolve) => {
                    const request = indexedDB.deleteDatabase(name);
                    request.onsuccess = () => resolve();
                    request.onerror = () => resolve();
                    // Another window still has it open. Not worth waiting for:
                    // the reload is what has to happen.
                    request.onblocked = () => resolve();
                }),
                RAW_READ_TIMEOUT_MS,
                undefined
            )
        )
    );
}

function primaryPathOf(collection: string): string {
    const config = entityConfigs.find((one) => one.collection === collection);
    const schema = config?.rxSchema as { primaryKey?: string } | undefined;
    return schema?.primaryKey ?? 'id';
}

function apiBaseOf(collection: string): string | null {
    return (
        entityConfigs.find((one) => one.collection === collection)?.apiBase ??
        null
    );
}

/**
 * How far apart the two clocks may be before their timestamps stop meaning
 * anything to each other. Five minutes covers ordinary drift; past it something
 * is actually wrong.
 */
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

function clocksAgree(response: Response): boolean {
    const stamp = response.headers.get('Date');
    if (!stamp) {
        // No header to judge by — a proxy stripped it. The comparison is no
        // worse off than it was before.
        return true;
    }
    const serverNow = Date.parse(stamp);
    if (!Number.isFinite(serverNow)) {
        return true;
    }
    return Math.abs(Date.now() - serverNow) <= MAX_CLOCK_SKEW_MS;
}

/**
 * The stamp two versions of a record are judged by. Compared as STRINGS, which
 * is chronological only because every one of these is `toISOString()`: UTC,
 * `Z`-suffixed, same shape. A local offset (`…+02:00`) would sort wrong against
 * a `…Z` one for the same instant.
 */
function updatedAtOf(doc: Record<string, unknown>): string {
    return typeof doc.updatedAt === 'string' ? doc.updatedAt : '';
}

/**
 * Every unpushed document this device still holds. The journal decides WHICH, or
 * a rescue would offer the server eight thousand documents it already has; the
 * content comes from the outbox, and from the raw stores where there is no copy.
 */
export async function collectUnsent(): Promise<SalvagedDoc[]> {
    const wanted = new Map<
        string,
        { collection: string; docId: string; userId: string }
    >();
    for (const entry of pendingEntries()) {
        wanted.set(`${entry.collection}/${entry.docId}`, entry);
    }
    if (wanted.size === 0) {
        return [];
    }

    const rescued = new Map<string, SalvagedDoc>();

    // The outbox first: it is the copy taken at the moment of the write, so it
    // cannot have been half-migrated by whatever went wrong afterwards.
    for (const entry of await readOutbox()) {
        const key = `${entry.collection}/${entry.docId}`;
        if (wanted.has(key)) {
            rescued.set(key, {
                collection: entry.collection,
                docId: entry.docId,
                userId: wanted.get(key)?.userId ?? '',
                doc: entry.doc,
                source: 'outbox',
                schemaVersion: entry.schemaVersion
            });
        }
    }

    // The raw stores are read ONLY for what the outbox could not answer: that
    // means `getAll` on every collection — several thousand documents, on the
    // thread drawing the screen — to pick out a handful of ids. Since every
    // local write leaves an outbox copy, normally nothing is missing.
    const missing = [...wanted.keys()].filter((key) => !rescued.has(key));
    if (missing.length === 0) {
        return [...rescued.values()];
    }

    for (const database of await rawDatabases()) {
        const primaryPath = primaryPathOf(database.collection);
        for (const doc of await readRawDocs(database.name)) {
            const docId = String(doc[primaryPath] ?? '');
            const key = `${database.collection}/${docId}`;
            if (!wanted.has(key)) {
                continue;
            }
            const already = rescued.get(key);
            // The outbox copy stands. Between two raw copies — one per schema
            // version — the newer document wins.
            if (
                already &&
                (already.source === 'outbox' ||
                    updatedAtOf(already.doc) >= updatedAtOf(doc))
            ) {
                continue;
            }
            rescued.set(key, {
                collection: database.collection,
                docId,
                userId: wanted.get(key)?.userId ?? '',
                doc,
                source: 'database',
                schemaVersion: database.version
            });
        }
    }
    return [...rescued.values()];
}

/**
 * Fields RxDB keeps for itself. The server has its own idea of a revision and
 * rejects a document carrying somebody else's, so they are stripped.
 */
function forTheWire(doc: Record<string, unknown>): Record<string, unknown> {
    const { _meta, _rev, _attachments, ...rest } = doc;
    void _meta;
    void _rev;
    void _attachments;
    return rest;
}

type PushOutcome =
    | { kind: 'sent' }
    /** The server holds a NEWER version; this copy is history, not pending. */
    | { kind: 'superseded' }
    /** Could not be placed right now. Keep it and try again later. */
    | { kind: 'stale'; reason: string };

export interface UploadReport {
    sent: SalvagedDoc[];
    superseded: SalvagedDoc[];
    stale: { doc: SalvagedDoc; reason: string }[];
    /** Nothing reached the server at all — offline, or no usable credential. */
    offline: boolean;
}

/**
 * The credential this document travels under — not simply "whoever is signed
 * in": each author's group goes up with their own kept token, so a tablet handed
 * over in the field does not file the previous user's Befunde under the new one.
 *
 * Null means "not now": their token is gone, so the work waits for them.
 */
function credentialFor(
    doc: SalvagedDoc,
    signedIn: string | null
): string | null {
    if (doc.userId === '' || (signedIn !== null && doc.userId === signedIn)) {
        return getToken();
    }
    const kept = keptCredentialFor(doc.userId);
    if (kept !== null) {
        return kept;
    }
    // Nobody could be identified at all. Its own token is the only one there is,
    // and sending under it beats leaving the work on a device about to be wiped.
    return signedIn === null ? getToken() : null;
}

/** Who the app believes is signed in; set by AuthProvider, above this screen. */
function signedInUserId(): string | null {
    return getCurrentAuthor();
}

/**
 * Offer one document to the server, over the replication's own route.
 *
 * `assumedMasterState` is left off the first attempt, since this device can no
 * longer say what it thought the server held. The server then conflicts on ANY
 * id it knows — which is not "yours is out of date", and treating it so threw
 * away every offline EDIT. So the answer is read and the two compared; ours
 * newer ⇒ sent again declaring the server's state as the base.
 */
async function pushOne(
    row: SalvagedDoc,
    apiBase: string,
    token: string | null
): Promise<PushOutcome> {
    const send = async (assumed?: Record<string, unknown>) =>
        fetchAs(token, `${apiBase}/push`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                // The shape this copy is in — possibly an older build's.
                schemaVersion: row.schemaVersion,
                docs: [
                    {
                        newDocumentState: forTheWire(row.doc),
                        ...(assumed ? { assumedMasterState: assumed } : {})
                    }
                ]
            })
        });

    let response: Response;
    try {
        response = await send();
    } catch {
        return { kind: 'stale', reason: 'Nicht erreichbar' };
    }
    // 401, 403, 500 — none of them mean the work is gone, and all of them used
    // to end with its last copy being deleted.
    if (!response.ok) {
        return {
            kind: 'stale',
            reason: `Server antwortete ${response.status}`
        };
    }

    const conflicts = (await response.json().catch(() => [])) as unknown;
    const theirs = Array.isArray(conflicts)
        ? (conflicts[0] as Record<string, unknown> | undefined)
        : undefined;
    if (!theirs) {
        return { kind: 'sent' };
    }

    // Which version is newer rests on a timestamp the DEVICE wrote, and a
    // device's clock is not a fact — this app has met the year 0024. So the
    // server's clock is asked first (the response's `Date` header); too far
    // apart and nothing is decided, the copy kept and put into the file.
    if (!clocksAgree(response)) {
        return {
            kind: 'stale',
            reason: 'Die Uhr des Geräts weicht vom Server ab'
        };
    }
    if (updatedAtOf(theirs) >= updatedAtOf(row.doc)) {
        return { kind: 'superseded' };
    }

    try {
        const second = await send(theirs);
        if (!second.ok) {
            return {
                kind: 'stale',
                reason: `Server antwortete ${second.status}`
            };
        }
        const again = (await second.json().catch(() => [])) as unknown;
        if (Array.isArray(again) && again.length > 0) {
            // Somebody wrote it again between the two attempts. Not settled:
            // the next run compares against whatever stands then.
            return {
                kind: 'stale',
                reason: 'Der Server hat sich zwischenzeitlich geändert'
            };
        }
        return { kind: 'sent' };
    } catch {
        return { kind: 'stale', reason: 'Nicht erreichbar' };
    }
}

/**
 * Offer the rescued work to the server, one document at a time — a batch that
 * tripped over a single row would take the rest of somebody's day with it.
 */
async function uploadUnsent(docs: SalvagedDoc[]): Promise<UploadReport> {
    const report: UploadReport = {
        sent: [],
        superseded: [],
        stale: [],
        offline: false
    };
    if (navigator.onLine === false) {
        report.offline = true;
        report.stale = docs.map((doc) => ({ doc, reason: 'Offline' }));
        return report;
    }

    const signedIn = signedInUserId();
    let reachedServer = false;
    for (const row of docs) {
        const apiBase = apiBaseOf(row.collection);
        if (!apiBase) {
            report.stale.push({ doc: row, reason: 'Unbekannter Bereich' });
            continue;
        }
        const token = credentialFor(row, signedIn);
        if (token === null) {
            // Their token is gone; their work waits for them to sign in, as the
            // ordinary push holds a blocked colleague's queue back. Counted as
            // having reached the server although it never left: "Ohne Verbindung"
            // on a tablet with four bars sends somebody after the wrong problem.
            reachedServer = true;
            report.stale.push({
                doc: row,
                reason: 'Wartet auf die Anmeldung des Verfassers'
            });
            continue;
        }

        const outcome = await pushOne(row, apiBase, token);
        if (outcome.kind === 'sent') {
            reachedServer = true;
            report.sent.push(row);
            continue;
        }
        if (outcome.kind === 'superseded') {
            reachedServer = true;
            report.superseded.push(row);
            continue;
        }
        if (outcome.reason !== 'Nicht erreichbar') {
            reachedServer = true;
        }
        report.stale.push({ doc: row, reason: outcome.reason });
    }
    report.offline = !reachedServer && docs.length > 0;
    return report;
}

async function markAsSent(docs: readonly SalvagedDoc[]): Promise<void> {
    if (docs.length === 0) {
        return;
    }
    forgetPending(docs);
    const byCollection = new Map<string, string[]>();
    for (const one of docs) {
        byCollection.set(one.collection, [
            ...(byCollection.get(one.collection) ?? []),
            one.docId
        ]);
    }
    for (const [collection, ids] of byCollection) {
        await dropFromOutbox(collection, ids);
    }
}

/**
 * Find the unsent work, try to get it to the server, and say what happened.
 *
 * What the server took, or has since overtaken, is marked as sent — journal
 * cleared, copy dropped — or the warning above the reset button would be a lie
 * in the direction that makes somebody press it. Nothing that did NOT get
 * through is touched.
 *
 * Photos go too, and LAST: their uploader (`features/media/blobStore`) is
 * normally started once the database is open, which here never happens, and a
 * binary is refused until its record exists on the server.
 */
export async function salvageUnsent(): Promise<SalvageResult> {
    const found = await collectUnsent();
    const photosFound = (await listPendingIds()).length;

    const report: UploadReport =
        found.length > 0
            ? await uploadUnsent(found)
            : { sent: [], superseded: [], stale: [], offline: false };

    await markAsSent([...report.sent, ...report.superseded]);

    if (photosFound > 0) {
        // Drains the queue and leaves behind whatever the server would not
        // take; it never throws and never deletes a blob it did not place.
        await kickUpload();
    }
    return {
        found,
        uploaded: report.sent.length,
        refused: [
            ...report.superseded.map((one) => ({
                collection: one.collection,
                docId: one.docId,
                reason: 'Der Server hat bereits einen neueren Stand'
            })),
            ...report.stale.map((one) => ({
                collection: one.doc.collection,
                docId: one.doc.docId,
                reason: one.reason
            }))
        ],
        offline: report.offline,
        photosFound,
        photosLeft: await photosStillWaiting(photosFound)
    };
}

/**
 * How many photos are still queued, asked twice: `kickUpload` returns at once
 * when a drain is already under way, so awaiting it does not mean the queue is
 * empty — counting straight afterwards told people their photos were stuck while
 * they were going up a second later.
 */
async function photosStillWaiting(before: number): Promise<number> {
    if (before === 0) {
        return 0;
    }
    const first = (await listPendingIds()).length;
    if (first === 0) {
        return 0;
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return (await listPendingIds()).length;
}

/**
 * Send up the copies of documents the local database no longer holds — a device
 * whose reset took the RxDB stores while outbox and journal survived, so the
 * work is still there but RxDB has nothing left to push.
 *
 * NOT only about documents that vanished: after a reset the server's version of
 * an edited record comes straight back, so the local document exists again as
 * the OLD state with the edit only in the outbox. The rule is therefore the copy
 * being NEWER than what is stored.
 *
 * Returns the keys that are SETTLED: taken, overtaken, or with no copy left.
 */
export async function uploadOrphans(
    entries: readonly {
        collection: string;
        docId: string;
        userId: string;
        /**
         * What the LOCAL database holds for this id, empty when it holds
         * nothing. The copy is only offered when it is newer — see above.
         */
        localUpdatedAt: string;
    }[]
): Promise<Set<string>> {
    const settled = new Set<string>();
    if (entries.length === 0) {
        return settled;
    }
    const wanted = new Map(
        entries.map((entry) => [`${entry.collection}/${entry.docId}`, entry])
    );
    const copies: SalvagedDoc[] = [];
    for (const entry of await readOutbox()) {
        const docKey = `${entry.collection}/${entry.docId}`;
        const asked = wanted.get(docKey);
        if (!asked) {
            continue;
        }
        // Nothing to add: the local database already holds this state or a
        // later one, so whatever is stuck is not stuck for want of this copy.
        if (
            asked.localUpdatedAt !== '' &&
            asked.localUpdatedAt >= updatedAtOf(entry.doc)
        ) {
            continue;
        }
        copies.push({
            collection: entry.collection,
            docId: entry.docId,
            userId: asked.userId,
            doc: entry.doc,
            source: 'outbox',
            schemaVersion: entry.schemaVersion
        });
    }

    // An entry with no copy worth sending cannot be helped by this. It is
    // settled in the only sense available: there is nothing left to try.
    const haveCopy = new Set(
        copies.map((one) => `${one.collection}/${one.docId}`)
    );
    for (const docKey of wanted.keys()) {
        if (!haveCopy.has(docKey)) {
            settled.add(docKey);
        }
    }
    if (copies.length === 0) {
        return settled;
    }

    const report = await uploadUnsent(copies);
    const done = [...report.sent, ...report.superseded];
    await markAsSent(done);
    for (const one of done) {
        settled.add(`${one.collection}/${one.docId}`);
    }
    return settled;
}

/**
 * The one grammar for these files — the archive, the documents inside it and the
 * copy behind a report mail's link. Dated first, because that is what a download
 * folder sorts by; English and ASCII, so it survives every mail client.
 */
function salvageFileName(extension: 'json' | 'zip'): string {
    // The app's own stamp, with seconds: two rescues can happen a breath apart.
    return `${fileNameTimestamp(new Date(), { seconds: true })}_AST-Data_unsynced-records.${extension}`;
}

function salvagePayload(docs: readonly SalvagedDoc[]): string {
    return JSON.stringify(
        {
            exportedAt: new Date().toISOString(),
            appVersion: __APP_VERSION__,
            note: 'AST Data — records from this device that never reached the server.',
            docs
        },
        null,
        2
    );
}

/** Past this the attachment is left off; the report itself still goes. */
const MAX_ATTACHMENT_CHARS = 4_000_000;

/**
 * The device's unsent work as a file to hang on the fault report. Built at the
 * moment of sending, never kept: a queued report waits in localStorage, which
 * holds five megabytes for the whole app. Null when there is nothing
 * outstanding, or when it is too big to mail — the report must arrive either way.
 */
export async function unsentAttachment(): Promise<{
    fileName: string;
    content: string;
} | null> {
    try {
        const found = await collectUnsent();
        if (found.length === 0) {
            return null;
        }
        const content = salvagePayload(found);
        if (content.length > MAX_ATTACHMENT_CHARS) {
            return null;
        }
        return { fileName: salvageFileName('json'), content };
    } catch {
        return null;
    }
}

/**
 * Past this the photos are left out of the file: `zipSync` builds the archive in
 * memory, and a tablet asked to hold half a gigabyte of JPEGs twice over does
 * not warn, it dies. The documents must get out either way.
 */
const MAX_PHOTO_BYTES = 300 * 1024 * 1024;

const PHOTO_EXTENSIONS: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp'
};

export interface SalvageFile {
    photos: number;
    photosOmitted: boolean;
}

/**
 * Hand the rescued work out as a file: the documents AND the photos. Where this
 * file is the answer at all, every other route out is closed — the blob upload
 * included — and a Befund without its photographs is half a record.
 *
 * They are NOT in the copy the server keeps: that one is for a device that DID
 * reach the server. A zip, stored rather than compressed: JPEGs do not compress.
 */
export async function downloadSalvage(
    docs: readonly SalvagedDoc[]
): Promise<SalvageFile> {
    // The documents carry the archive's dated name, so a file pulled out of one
    // and mailed on still says what it is and when.
    const documentsName = salvageFileName('json');
    const files: Record<string, Uint8Array> = {
        [documentsName]: new TextEncoder().encode(salvagePayload(docs))
    };

    let photos = 0;
    let bytes = 0;
    let photosOmitted = false;
    try {
        for (const id of await listPendingIds()) {
            const entry = await getPending(id);
            if (!entry) {
                continue;
            }
            bytes += entry.blob.size;
            if (bytes > MAX_PHOTO_BYTES) {
                photosOmitted = true;
                break;
            }
            const extension = PHOTO_EXTENSIONS[entry.mimeType] ?? 'bin';
            files[`fotos/${id}.${extension}`] = new Uint8Array(
                await entry.blob.arrayBuffer()
            );
            photos += 1;
        }
    } catch {
        // The blob store is unavailable; the documents still go out.
    }

    downloadBlob(await zipToBlob(files, { level: 0 }), salvageFileName('zip'));
    return { photos, photosOmitted };
}
